/**
 * Feefo Reviews - Vanilla JS Version (Final)
 * Compatible with Shopify / BigCommerce
 * Supports: Service Summary, Service Reviews, Product Rating, Product Reviews
 * Version: 1.2 Final
 */

function initFeefoVanilla(serviceMerchantId, productMerchantId) {
  if (!serviceMerchantId && !productMerchantId) return;

  class FeefoReviews {
    constructor(serviceId, productId, since = 'all') {
      this.serviceId = serviceId;
      this.productId = productId || serviceId;
      this.sincePeriod = since;
    }

    getApiUrl(path) {
      return `https://api.feefo.com/api/10${path}`;
    }

    escapeHTML(str) {
      if (!str) return '';
      return str.replace(/[&<div>"']/g, (m) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[m]));
    }

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    getStarsHtml(rating) {
      const maxStars = 5;
      let html = '';
      for (let i = 1; i <= maxStars; i++) {
        html += i <= rating
          ? '<span class="icon icon--ratingFull"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="icon-tabler icons-tabler-filled icon-tabler-star"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z" /></svg></span>'
          : '<span class="icon icon--ratingEmpty"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="icon-tabler icons-tabler-filled icon-tabler-star"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z" /></svg></span>';
      }
      return html;
    }

    async fetchJSON(url, params = {}) {
      const u = new URL(url);
      Object.keys(params).forEach(k => params[k] !== undefined && u.searchParams.append(k, params[k]));
      const res = await fetch(u);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    }

    createPagination(container, totalPages, currentPage, onClick) {
      if (totalPages <= 1) return;
      const old = container.querySelector('.feefo-pagination');
      if (old) old.remove();

      const div = document.createElement('div');
      div.className = 'feefo-pagination';
      const ul = document.createElement('ul');
      ul.className = 'pagination-list';

      const startPage = Math.max(currentPage - 2, 1);
      const endPage = Math.min(currentPage + 2, totalPages);

      if (startPage > 1) {
        ul.innerHTML += `<li class="pagination-item"><a href="#" data-page="1">1</a></li>`;
        if (startPage > 2) ul.innerHTML += `<li class="pagination-item no-link">...</li>`;
      }

      for (let i = startPage; i <= endPage; i++) {
        ul.innerHTML += `<li class="pagination-item ${i===currentPage?'pagination-item--current':''}"><a href="#" data-page="${i}" class="pagination-link ${i===currentPage?'current':''}">${i}</a></li>`;
      }

      if (endPage < totalPages) {
        if (endPage < totalPages-1) ul.innerHTML += `<li class="pagination-item no-link">...</li>`;
        ul.innerHTML += `<li class="pagination-item"><a href="#" data-page="${totalPages}">${totalPages}</a></li>`;
      }

      ul.addEventListener('click', (e) => {
        e.preventDefault();
        const a = e.target.closest('a[data-page]');
        if (!a) return;
        onClick(parseInt(a.dataset.page));
      });

      div.appendChild(ul);
      container.appendChild(div);
    }

    // ----------------------------
    // Service Summary
    // ----------------------------
    async renderServiceSummary(container) {
      try {
        const url = this.getApiUrl('/importedreviews/summary/service');
        const data = await this.fetchJSON(url, { merchant_identifier: this.serviceId, since_period: this.sincePeriod });
        const rating = data.rating?.rating || 0;
        const maxRating = data.rating?.max || 5;
        const count = data.meta?.count || 0;

        container.innerHTML = `
          <div class="feefo-stars">${this.getStarsHtml(rating)}</div>
          <div class="rating-number">${rating} / ${maxRating}</div>
          <a href="/pages/customer-reviews" class="review-count">From ${count} Reviews</a>
        `;
      } catch (err) {
        console.error('Service Summary Error:', err);
        container.innerHTML = '<p>Unable to load reviews summary.</p>';
      }
    }

    // ----------------------------
    // Service Reviews
    // ----------------------------
    async renderServiceReviews(container, page = 1, pageSize = 9, options = {}) {
      try {
        const disablePagination = options.disablePagination || false;
        const ratings = options.ratings || '';

        const url = this.getApiUrl('/importedreviews/service');
        const data = await this.fetchJSON(url, {
          merchant_identifier: this.serviceId,
          since_period: this.sincePeriod,
          page,
          page_size: pageSize,
          rating: ratings || undefined,
          full_thread: 'include'
        });

        const reviews = data.reviews || [];
        const meta = data.summary?.meta || { pages: 1 };

        if (!reviews.length) {
          container.innerHTML = '<p>No reviews yet.</p>';
          return;
        }

        container.innerHTML = reviews.map(r => {
          const review = r.service || {};
          const title = review.title || '';
          const author = r.customer?.display_name || 'Trusted Customer';
          const date = this.formatDate(review.created_at);
          const stars = this.getStarsHtml(review.rating?.rating || 0);

          let product = '';
          if (r.products_purchased?.length) {
            product = `
              <span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="icon-tabler icons-tabler-filled icon-tabler-tag"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M11.172 2a3 3 0 0 1 2.121 .879l7.71 7.71a3.41 3.41 0 0 1 0 4.822l-5.592 5.592a3.41 3.41 0 0 1 -4.822 0l-7.71 -7.71a3 3 0 0 1 -.879 -2.121v-5.172a4 4 0 0 1 4 -4zm-3.672 3.5a2 2 0 0 0 -1.995 1.85l-.005 .15a2 2 0 1 0 2 -2" /></svg></span>
              <span class="feefo-product">${r.products_purchased[0]}</span>
            `;
          }

          return `
            <div class="feefo-service-review">
              <article>
                <header>
                  <span class="feefo-stars">${stars}</span>
                  <p class="feefo-title"><strong>${title}</strong></p>
                  <p class="feefo-author">Posted by ${author} on ${date}</p>
                  <p class="feefo-products">${product}</p>
                </header>
                <p class="feefo-body">${review.review || ''}</p>
              </article>
            </div>
          `;
        }).join('');

        if (!disablePagination) {
          this.createPagination(container, meta.pages, page, (p) => {
            this.renderServiceReviews(container, p, pageSize, options);
          });
        }
      } catch (err) {
        console.error('Service Reviews Error:', err);
        container.innerHTML = '<p>Unable to load service reviews.</p>';
      }
    }

    // ----------------------------
    // Product Rating
    // ----------------------------
    async renderProductRating(container, productId, productSku) {
      try {
        const url = this.getApiUrl('/importedreviews/summary/product');
        const data = await this.fetchJSON(url, {
          merchant_identifier: this.productId,
          since_period: this.sincePeriod,
          product_sku: productSku,
          parent_product_sku: productId
        });

        const rating = data.rating?.rating || 0;
        const count = data.meta?.count || 0;
        const countTotal = count === 1 ? `${count} review` : `${count} reviews`;
        container.innerHTML = `<div><span class="feefo-stars">${this.getStarsHtml(rating)}</span><span>&nbsp;(<a href="#product-reviews">${countTotal}</a>)</span></div>`;
      } catch (err) {
        console.error('Product Rating Error:', err);
      }
    }

    // ----------------------------
    // Card Product Rating
    // ----------------------------
    async renderCardProductRating(container, productId, productSku) {
      try {
        const url = this.getApiUrl('/importedreviews/summary/product');
        const data = await this.fetchJSON(url, {
          merchant_identifier: this.productId,
          since_period: this.sincePeriod,
          product_sku: productSku,
          parent_product_sku: productId
        });

        const rating = data.rating?.rating || 0;
        const count = data.meta?.count || 0;

        if (count === 0) {
          container.innerHTML = `<div><span class="feefo-stars">${this.getStarsHtml(rating)}</span></div>`;
        }else {
          container.innerHTML = `<div><span class="feefo-stars">${this.getStarsHtml(rating)}</span><span>&nbsp;(${count})</span></div>`;
        }
      } catch (err) {
        console.error('Product Rating Error:', err);
      }
    }

    // ----------------------------
    // Product Reviews
    // ----------------------------
    async renderProductReviews(container, productId, productSku, page = 1, pageSize = 9) {
      try {
        const url = this.getApiUrl('/importedreviews/product');
        const data = await this.fetchJSON(url, {
          merchant_identifier: this.productId,
          since_period: this.sincePeriod,
          product_sku: productSku,
          parent_product_sku: productId,
          page,
          page_size: pageSize,
          full_thread: 'include'
        });

        const reviews = data.reviews || [];
        const meta = data.summary?.meta || { pages: 1 };
        const count = meta.count;
        const countTitle = count === 1 ? `${count} Review` : `${count} Reviews`;

        if (!reviews.length) {
          container.innerHTML = '<p>No reviews yet.</p>';
          return;
        }

        container.innerHTML = `
          <div class="feefo-summary">
            <div class="count-reviews">${countTitle}</div>
          </div>
          <div class="feefo-reviews-list">
            ${reviews.map(r => {
              const review = r.products?.[0] || {};
              const author = r.customer?.display_name || 'Trusted Customer';
              const date = this.formatDate(review.created_at);
              const stars = this.getStarsHtml(review.rating?.rating || 0);

              return `
                <div class="feefo-review">
                  <header>
                    <span class="feefo-stars">${stars}</span>
                    <p class="feefo-author">Posted by ${author || ''} on ${date}</p>
                  </header>
                  <p class="feefo-body">${review.review || ''}</p>
                </div>
              `;
            }).join('')}
          </div>
        `;
      } catch (err) {
        console.error('Product Reviews Error:', err);
        container.innerHTML = '<p>Unable to load product reviews.</p>';
      }
    }
  }

  const feefo = new FeefoReviews(serviceMerchantId, productMerchantId);

  // Service Summary
  document.querySelectorAll('.feefo-service-summary-widget')
    .forEach(el => feefo.renderServiceSummary(el));

  // Service Reviews
  document.querySelectorAll('.feefo-review-widget-service')
    .forEach(el => {
      const pageSize = parseInt(el.dataset.pageSize, 10) || 9;
      const ratings = el.dataset.ratings || '';
      const isHomepage = el.dataset.homepage === 'true';
      feefo.renderServiceReviews(el, 1, pageSize, { ratings, disablePagination: isHomepage });
    });

  // Product Rating
  document.querySelectorAll('.feefo-product-rating-widget')
    .forEach(el => {
      const pid = el.dataset.productId;
      const sku = el.dataset.productSku;
      feefo.renderProductRating(el, pid, sku);
    });

  // Card Product Rating
  document.querySelectorAll('.feefo-card-product-rating')
    .forEach(el => {
      const pid = el.dataset.productId;
      const sku = el.dataset.productSku;
      feefo.renderCardProductRating(el, pid, sku);
    });

  // Product Reviews
  document.querySelectorAll('.feefo-review-widget-product')
    .forEach(el => {
      const pid = el.dataset.productId;
      const sku = el.dataset.productSku;
      feefo.renderProductReviews(el, pid, sku);
    });
}

// -----------------------------------
// Initialize on DOM Ready
// -----------------------------------
document.addEventListener('DOMContentLoaded', function() {
  initFeefoVanilla('leather-case-store-australia', 'case-store-group');
});
